import React, { memo } from "react";
import { TableRow } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import IconButton from "../../../../shared/components/IconButton";
import CustomTable from "../../../../shared/components/CustomTable";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import { api } from "../../../../shared/hooks/api";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { buildInventoryModalSearchParams } from "../../../../utils/inventoryModalSearchParams";
import { error, warning } from "../../../../utils/toast";

interface ExportMaterialListProps {
    selectedMaterials: any[];
    setSelectedMaterials: Function;
}

interface MaterialRowProps {
    item: any;
    extraRowProps: any;
}

export const TableColumns = [
    { text: "Vật tư", icon: null },
    { text: "Khả dụng", icon: null },
    { text: "Số lượng xuất", icon: null },
    { text: "Ghi chú", icon: null },
    { text: "", icon: null },
];

const MaterialRow = memo(({ item, extraRowProps }: MaterialRowProps) => {
    return (
        <TableRow>
            <StyledTableCell>{item.name}</StyledTableCell>
            <StyledTableCell>
                {formatInternationalNumber(item.available_quantity ?? item.quantity ?? 0)}
            </StyledTableCell>
            <StyledTableCell>
                <TextBox
                    numeric
                    text={formatInternationalNumber(item.exportQuantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeExportQuantity(item.id, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <TextBox text={item.note || ""}
                         onChangeText={(value: string) => extraRowProps.onChangeNote(item.id, value)} />
            </StyledTableCell>
            <StyledTableCell>
                <IconButton onClick={() => extraRowProps.removeSelectedItem(item.id)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
});

MaterialRow.displayName = "MaterialRow";

export const ExportMaterialList = ({ selectedMaterials, setSelectedMaterials }: ExportMaterialListProps) => {
    const [{ data }, fetchMaterials] = api.get(
        "/materials/",
        buildInventoryModalSearchParams(),
        true,
    );

    const materials = data && data.results ? data.results : [];

    const fetchItems = (variables: any) => {
        fetchMaterials(buildInventoryModalSearchParams(variables), true);
    };

    const handleOnclickItem = (item: any, checked: boolean) => {
        let updatedMaterials;
        if (checked) {
            if (!selectedMaterials.find((material) => material.id === item.id)) {
                updatedMaterials = [...selectedMaterials, { ...item, exportQuantity: 0, note: "" }];
            } else {
                updatedMaterials = [...selectedMaterials];
            }
        } else {
            updatedMaterials = selectedMaterials.filter((material) => material.id !== item.id);
        }
        setSelectedMaterials(updatedMaterials);
    };

    const onChangeExportQuantity = (id: string, quantity: number) => {
        const selectedMaterial = selectedMaterials.find(
            (material: any) => material.id === id,
        );
        const availableQuantity =
            selectedMaterial?.available_quantity ?? selectedMaterial?.quantity ?? 0;
        if (quantity > availableQuantity) {
            warning(
                "Số lượng xuất vượt quá số lượng khả dụng",
                `Khả dụng: ${formatInternationalNumber(availableQuantity)}`,
            );
            return;
        }
        const updatedMaterials = selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, exportQuantity: quantity } : material,
        );
        setSelectedMaterials(updatedMaterials);
    };

    const onChangeNote = (id: string, note: string) => {
        const updatedMaterials = selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, note } : material,
        );
        setSelectedMaterials(updatedMaterials);
    };

    const removeSelectedItem = (id: string) => {
        const updatedMaterials = selectedMaterials.filter((selectedItem: any) => selectedItem.id !== id);
        setSelectedMaterials(updatedMaterials);
    };

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
            <SearchBarWithSuggestion
                menuList={materials}
                selectedItems={selectedMaterials}
                placeholder="Tìm kiếm vật tư"
                fetchItems={fetchItems}
                handleOnclickItem={handleOnclickItem}
            />
            <CustomTable
                itemList={selectedMaterials}
                TableColumns={TableColumns}
                RowElement={MaterialRow}
                extraRowProps={{
                    onChangeExportQuantity,
                    onChangeNote,
                    removeSelectedItem,
                }}
            />
        </div>
    );
};
